import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth, dropViewAsStash } from './AuthContext';
import { resolveApiBaseUrl } from '../config/api';

/**
 * PlatformAuthContext — post-v15 shim.
 *
 * Pre-v15 there were two separate auth systems (users vs platform_users
 * tables, with their own tokens). v15 collapsed them into one. This
 * context survives as a delegating shim so the existing platform admin
 * pages (PlatformDashboard, PlatformClubs, PlatformStaff, etc.) don't
 * need rewriting in one sweep.
 *
 * Under the hood:
 *   - Login hits the unified /api/auth/login endpoint.
 *   - The token is stored under the same `marakin_token` key as regular
 *     auth so the two contexts share state.
 *   - `platformUser` is populated when the authenticated user has
 *     `platformRole` set in the unified /me response.
 *   - The deprecated `marakin_platform_token` key is opportunistically
 *     cleaned up.
 */

const PlatformAuthContext = createContext(null);

// Legacy axios instance still pointed at /api/platform/* (PlatformDashboard
// etc. call `platformAxios.get('/clubs')` and expect that prefix). The
// underlying token is now the unified one in localStorage('marakin_token').
// B-33 (Week 4 store build): the base is resolved like the global axios —
// relative '/api' on web, the absolute REACT_APP_API_URL in the native shell,
// where a relative path would hit the bundled app instead of the backend.
const platformAxios = axios.create({ baseURL: `${resolveApiBaseUrl()}/platform` });

// B-3 ROOT CAUSE (26 Aug): this used to set the header on `platformAxios`
// only — but the boot effect below calls `/auth/me` on the GLOBAL axios,
// whose Authorization header is set by AuthProvider's own effect. React
// runs child effects BEFORE parent effects, and PlatformAuthProvider is
// nested inside AuthProvider, so on every FULL PAGE LOAD this ran first,
// fired /auth/me with no bearer token, took the 401 in the swallowing
// .catch(), and left platformUser null → PlatformProtectedRoute bounced a
// perfectly valid staff session to /marakin-admin/login. SPA navigation
// never remounts the provider, which is why only hard loads broke — and
// why Exit (a window.location.href) looked like an impersonation bug.
// The v31 attempt juggled token keys and could not have fixed it.
// Setting the global header here removes the ordering dependency entirely.
// (WT-1: the boot no longer calls /auth/me here at all; see below.)
function applyToken(token) {
  if (token) {
    platformAxios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete platformAxios.defaults.headers.common['Authorization'];
    delete axios.defaults.headers.common['Authorization'];
  }
}

export const PlatformAuthProvider = ({ children }) => {
  const [platformUser, setPlatformUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { syncPlatformUser, user, loading: authLoading, logout: authLogout } = useAuth();
  const booted = useRef(false); // WT-1: the boot answer is taken once

  // WT-1: take the boot answer from AuthProvider's /auth/me (the unified one
  // — it returns isPlatformUser + platformRole) instead of a second call of
  // our own. Ours gave up on the FIRST failure: platformUser stayed null, so
  // with no signal, a 429 or a 503 PlatformProtectedRoute bounced a good
  // staff session to the login page. AuthProvider's keeps `loading` true and
  // retries until it gets an answer, and a token-shaped 401 still ends the
  // session there (W-10). One request instead of two, too. (Declared before
  // the migration shim; it waits for AuthProvider either way.)
  useEffect(() => {
    if (booted.current || authLoading) return;
    booted.current = true;
    if (user?.isPlatformUser) {
      setPlatformUser({
        id: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.platformRole,
      });
    }
    setLoading(false);
  }, [authLoading, user]);

  useEffect(() => {
    // Migration shim: pre-v15 stored under marakin_platform_token. Promote
    // it to the unified key (idempotent) so legacy sessions keep working.
    const legacyToken = localStorage.getItem('marakin_platform_token');
    if (legacyToken && !localStorage.getItem('marakin_token')) {
      localStorage.setItem('marakin_token', legacyToken);
    }
    localStorage.removeItem('marakin_platform_token');

    const token = localStorage.getItem('marakin_token');
    if (token) applyToken(token);
  }, []);

  // Password and Google sign-in both end here with the login body.
  const startSession = async ({ token, user }) => {
    if (!user?.isPlatformUser) {
      throw new Error('This account is not a Marakin platform admin');
    }
    // B-73: a staff sign-in replaces whatever this tab held, so a "view as"
    // stash (the staffer's earlier token, the page to return to) must go too,
    // as AuthContext's sign-ins do (forgetSessionLeftovers). Only once the
    // staff session is real: a refused sign-in leaves a running view-as alone.
    dropViewAsStash();
    localStorage.setItem('marakin_token', token);
    localStorage.removeItem('marakin_platform_token');
    applyToken(token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    const platformShape = {
      id: user.id,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.platformRole,
    };
    setPlatformUser(platformShape);

    // Keep AuthContext in sync so the same browser session can navigate
    // between platform admin pages and club hub pages without re-authing.
    // B-73 security QA: the sign-in body's user is the fallback if /auth/me
    // fails, so the club side never keeps showing the previous identity.
    if (syncPlatformUser) await syncPlatformUser(user);

    return platformShape;
  };

  const login = async (email, password) => {
    // Hit the unified login endpoint.
    const res = await axios.post('/auth/login', { email, password });
    return startSession(res.data);
  };

  // B-68: Continue with Google, @marakin.co.uk Workspace accounts only. The
  // server refuses anyone who isn't staff (staffOnly) before any link or
  // sign-up step, so a success here is always a staff session.
  const googleSignIn = async (credential) => {
    const res = await axios.post('/auth/social', { ...credential, staffOnly: true });
    // Never expected with staffOnly; SocialSignIn hands these to onNeedsProfile.
    if (res.data?.needsProfile || res.data?.needsEmailCode) return { ...res.data };
    return { user: await startSession(res.data) };
  };

  // B-65 addition: after Change password the server hands back a new token
  // (the old one is now refused); the staff portal's own axios needs it too.
  const adoptToken = (token) => {
    localStorage.setItem('marakin_token', token);
    applyToken(token);
  };

  // B-73 security QA: one sign-out path. AuthContext's logout() removes the
  // token, any "view as" stash (which can hold the staffer's own 7-day
  // token), the "Viewing as" state and the query cache; this used to leave
  // all but the token behind.
  const logout = () => {
    authLogout();
    applyToken(null); // the staff portal's own axios too
    setPlatformUser(null);
  };

  return (
    <PlatformAuthContext.Provider value={{ platformUser, loading, login, googleSignIn, logout, platformAxios, adoptToken }}>
      {children}
    </PlatformAuthContext.Provider>
  );
};

export const usePlatformAuth = () => {
  const ctx = useContext(PlatformAuthContext);
  if (!ctx) throw new Error('usePlatformAuth must be used within PlatformAuthProvider');
  return ctx;
};

export { platformAxios };
